import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { appsCoreApiRouterGetRichContent, RichContentDetailOut } from '@/lib/api';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar, User, Tag, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/layout/Navbar';

const LogBookDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [entry, setEntry] = useState<RichContentDetailOut | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLogBookEntry = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const response = await appsCoreApiRouterGetRichContent(parseInt(id));
        setEntry(response.data);
      } catch (error) {
        console.error('Error fetching log book entry:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLogBookEntry();
  }, [id]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-adv-dark">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="mb-6">
            <Skeleton className="h-8 w-32 mb-4" />
            <Skeleton className="h-6 w-48" />
          </div>
          <Card className="glass">
            <CardHeader>
              <Skeleton className="h-8 w-3/4 mb-4" />
              <div className="flex gap-4">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-32" />
              </div>
            </CardHeader>
            <CardContent>
              <Skeleton className="h-64 w-full mb-4" />
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-2/3" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="min-h-screen bg-adv-dark">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="glass rounded-xl p-8">
              <h3 className="heading-md text-white mb-4">Entry Not Found</h3>
              <p className="text-adv-lightGray mb-6">
                The log book entry you're looking for doesn't exist or has been removed.
              </p>
              <Button
                variant="outline"
                onClick={() => navigate('/content-garage')}
                className="text-adv-accent border-adv-accent hover:bg-adv-accent/10"
              >
                <ArrowLeft size={16} className="mr-2" />
                Back to Content Garage
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-adv-dark pt-24">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        {/* Back Link */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/content-garage')}
            className="text-adv-accent border-adv-accent hover:bg-adv-accent/10 mb-4"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back to Content Garage
          </Button>
        </div>

        {/* Entry Content */}
        <Card className="glass">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex-1">
                <h1 className="heading-lg mb-4">{entry.title}</h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-adv-lightGray">
                  <div className="flex items-center gap-1">
                    <User size={14} />
                    <span>{entry.author_name}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar size={14} />
                    <span>{formatDate(entry.created_at)}</span>
                  </div>
                  {entry.tag_list.length > 0 && (
                    <div className="flex items-center gap-1">
                      <Tag size={14} />
                      <div className="flex gap-1">
                        {entry.tag_list.map((tag, index) => (
                          <Badge key={index} variant="secondary" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {entry.is_featured && (
                <Badge variant="default">
                  Featured
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {entry.image_url && (
                <div className="aspect-video rounded-lg overflow-hidden">
                  <img
                    src={entry.image_url}
                    alt={entry.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              
              {entry.content && (
                <div 
                className="ql-editor"
                dangerouslySetInnerHTML={{ __html: entry.content }}
                />
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LogBookDetail; 